/**
 * Where this engagement is, and roughly how much further it has to go.
 *
 * WHY THIS EXISTS
 * ---------------
 * The operator's own summary of everything wrong with this screen: "we need
 * clear intention of how many turns etc. we are predicting so we know when the
 * finish line is." An agentic engagement alternates between a model thinking
 * and a human deciding, and from the outside both look like a screen that is
 * not moving. Without a stated end the only available reading of a quiet screen
 * is that it has hung, which is why every stall report in the backlog arrived
 * alongside a press of Run next step.
 *
 * IT IS AN ESTIMATE AND IT SAYS SO
 * --------------------------------
 * There is no schedule to read: the Lead decides what to do next each turn, so
 * the true remaining count is unknowable. What IS knowable is the work already
 * agreed - angles queued, a plan approved - and what that has historically cost
 * per angle on this engagement. So the number is arithmetic over committed work
 * and is labelled "about", never "remaining".
 *
 * MEASURED, NOT GUESSED, WHEREVER IT CAN BE. Turns per angle comes from this
 * engagement's own finished workers first, and only falls back to a constant
 * when nothing has finished yet. A constant that disagrees with the run in
 * front of the operator is the same defect as the frozen 0% confidence: a
 * number that does not describe the thing it sits next to.
 */

/**
 * Turns an angle costs when this engagement has no history to measure.
 *
 * From the four engagements driven end to end: a worker takes a probe turn, a
 * conclude turn, and usually one more to widen a population or answer the
 * refuter. Three is the floor and it is also the mode.
 */
export const DEFAULT_TURNS_PER_ANGLE = 4;

/** The Lead's own overhead after the last angle lands: read the verdicts, close. */
const LEAD_WRAP_UP_TURNS = 2;

/**
 * The stages of an engagement, in the order they happen.
 *
 * DELIBERATELY COARSE. This is not a wizard and the runtime is not obliged to
 * follow it: a Lead may draft a second recon plan at turn 30, and the honest
 * thing for the screen to do is to move back a stage rather than to pretend. So
 * the stage is DERIVED on every render from the same overview the rest of the
 * Bench reads, and nothing is stored.
 */
export const STAGES = [
  { key: 'start', label: 'Start the Lead' },
  { key: 'recon_plan', label: 'Approve the recon plan' },
  { key: 'recon', label: 'Recon runs' },
  { key: 'test_plan', label: 'Approve the test plan' },
  { key: 'testing', label: 'Agents test the angles' },
  { key: 'report', label: 'Read the report' },
];

function stageKey({ overview, hypotheses }) {
  const plan = overview?.plan || null;
  const counts = overview?.counts || {};
  const lead = (overview?.roster || []).find((m) => m.role === 'lead');
  const working = (counts.queued || 0) + (counts.investigating || 0);
  const settled = (counts.confirmed || 0) + (counts.refuted || 0)
    + (counts.abandoned || 0) + (counts.blocked || 0);

  if (!lead && !plan) return 'start';
  if (plan?.status === 'awaiting_approval') {
    return plan.kind === 'recon' ? 'recon_plan' : 'test_plan';
  }
  if (plan?.status === 'approved' && plan.kind === 'recon') {
    const steps = plan.recon_steps || 0;
    const done = (plan.recon_finished || 0) + (plan.recon_skipped || 0);
    if (steps && done >= steps) return 'test_plan';
    return 'recon';
  }
  if (working) return 'testing';
  if (settled || (hypotheses || []).length) return 'report';
  return 'start';
}

/**
 * Is the engagement over?
 *
 * ALL THREE TERMS MATTER and each was a bug on its own. A complete Lead with
 * angles still queued happened on pentest 44 (the Lead closed, a worker was
 * mid-run). A plan at the approval gate with everything else settled is
 * pentest 41, parked on purpose. And an engagement with no Lead at all has not
 * finished, it has not begun, which is the state that produced a report headed
 * "0 confirmed" on a fresh engagement.
 */
export function isFinished({ overview, hypotheses }) {
  const lead = (overview?.roster || []).find((m) => m.role === 'lead');
  if (!lead) return false;
  if (lead.status !== 'complete') return false;
  if (overview?.plan?.status === 'awaiting_approval') return false;
  const counts = overview?.counts || {};
  if ((counts.queued || 0) + (counts.investigating || 0) > 0) return false;
  if ((overview?.awaiting_authority?.length || 0) > 0) return false;
  if ((overview?.awaiting_operator?.length || 0) > 0) return false;
  return true;
}

/**
 * Why the engagement is not finished, in the operator's words.
 *
 * Returns null when it IS finished. One sentence, naming the thing to do:
 * "still running" was what the screen said before, and it is the one piece of
 * information the operator already had.
 */
export function blockedBy({ overview, hypotheses }) {
  const counts = overview?.counts || {};
  const lead = (overview?.roster || []).find((m) => m.role === 'lead');
  const plan = overview?.plan || null;

  if (!lead && !plan) return 'This engagement has not been started yet.';
  if (plan?.status === 'awaiting_approval') {
    return `Plan v${plan.version} is waiting for your approval.`;
  }
  if ((overview?.awaiting_operator?.length || 0) > 0) {
    return 'The Lead has reported and is waiting to be read.';
  }
  if ((overview?.awaiting_authority?.length || 0) > 0) {
    return 'The Lead is waiting on a decision from you.';
  }
  const working = (counts.queued || 0) + (counts.investigating || 0);
  if (working) {
    return `${working} angle${working === 1 ? '' : 's'} still to be tested.`;
  }
  if (lead && lead.status !== 'complete') {
    return 'The Lead has not closed the engagement yet.';
  }
  return null;
}

/**
 * The whole estimate, as data.
 *
 * `plans` is the version history, used only for its length: "three plans" is
 * the number the operator asked to see, because it is the honest measure of how
 * many times the engagement has been round the loop and it was invisible.
 */
export function forecast({ overview, hypotheses = [], plans = [] }) {
  const counts = overview?.counts || {};
  const roster = overview?.roster || [];

  const turnsUsed = roster.reduce((n, m) => n + (m.turns_used || 0), 0);

  const outstanding = (counts.queued || 0) + (counts.investigating || 0)
    + (counts.planned || 0);
  const settled = (counts.confirmed || 0) + (counts.refuted || 0)
    + (counts.abandoned || 0) + (counts.blocked || 0);

  // MEASURED FIRST. Workers that have finished an angle on THIS engagement are
  // the best available predictor of the next one, and they are sitting in the
  // roster already.
  const finishedWorkers = roster.filter(
    (m) => m.role !== 'lead' && m.hypothesis_id && (m.turns_used || 0) > 0
      && m.status !== 'running',
  );
  const perAngle = finishedWorkers.length
    ? Math.max(1, Math.round(
      finishedWorkers.reduce((n, m) => n + m.turns_used, 0) / finishedWorkers.length,
    ))
    : DEFAULT_TURNS_PER_ANGLE;

  const finished = isFinished({ overview, hypotheses });
  // The wrap-up is added whether or not anything is outstanding, because an
  // engagement with nothing left to test still owes the operator the Lead turn
  // that reads the verdicts and closes it. That turn is exactly where pentest
  // 42 stalled, so leaving it out of the count would predict a finish line one
  // turn before the one the operator was waiting for.
  const turnsLeft = finished ? 0 : outstanding * perAngle + LEAD_WRAP_UP_TURNS;

  const key = stageKey({ overview, hypotheses });
  const index = Math.max(0, STAGES.findIndex((s) => s.key === key));

  return {
    stage: STAGES[index],
    stageNumber: index + 1,
    stageCount: STAGES.length,
    turnsUsed,
    turnsLeft,
    perAngle,
    // Whether the per-angle figure was measured on this engagement or assumed.
    perAngleMeasured: finishedWorkers.length > 0,
    outstanding,
    settled,
    planCount: plans.length || (overview?.plan ? overview.plan.version : 0),
    finished,
    blockedBy: blockedBy({ overview, hypotheses }),
  };
}

/** "3 plans", pluralised. Used in three places and got it wrong in two. */
export function plural(n, word, suffix = 's') {
  return `${n} ${word}${n === 1 ? '' : suffix}`;
}

/**
 * One line: where we are, and about how much further.
 *
 * Rendered under the next-step banner and at the top of the Report tab, which
 * are the two places the operator asked the question from.
 */
export default function Forecast({ overview, hypotheses, plans, className = '' }) {
  const f = forecast({ overview, hypotheses, plans });

  return (
    <div
      data-testid="forecast"
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500 ${className}`}
    >
      <span>
        Step <span className="font-semibold text-slate-700">{f.stageNumber}</span> of{' '}
        {f.stageCount}: {f.stage.label}
      </span>
      <span className="text-slate-300">|</span>
      <span>
        {plural(f.planCount, 'plan')} so far
      </span>
      <span className="text-slate-300">|</span>
      <span>{plural(f.turnsUsed, 'turn')} used</span>
      {!f.finished && (
        <>
          <span className="text-slate-300">|</span>
          <span
            title={f.perAngleMeasured
              ? `Estimated from this engagement's own finished agents: about ${f.perAngle} turns per angle.`
              : `Nothing has finished yet, so this assumes about ${f.perAngle} turns per angle from previous engagements.`}
          >
            about <span className="font-semibold text-slate-700">{f.turnsLeft}</span>{' '}
            more {f.turnsLeft === 1 ? 'turn' : 'turns'} to go
          </span>
        </>
      )}
      {f.finished && (
        <>
          <span className="text-slate-300">|</span>
          <span className="font-semibold text-wasabi-700">finished</span>
        </>
      )}
    </div>
  );
}
